import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Check, X, ExternalLink, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { actionCenterAPI, hrLifecycleAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { confirmToast } from '../lib/confirmToast';
import EmployeeTimeline from './EmployeeTimeline';

const APPROVER_ROLES = ['workspace_admin', 'admin', 'hr_admin'];

// Bucket display metadata. Order here drives the summary-card order.
const BUCKETS = [
  { key: 'lifecycle_approvals', label: 'Lifecycle Approvals', variant: 'default' },
  { key: 'exit_cases', label: 'Exit Cases', variant: 'destructive' },
  { key: 'expiring_contracts', label: 'Expiring Contracts', variant: 'secondary' },
  { key: 'draft_contracts', label: 'Draft Contracts', variant: 'outline' },
  { key: 'probation_due', label: 'Probation Due', variant: 'secondary' },
];
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map((b) => [b.key, b.label]));
const BUCKET_VARIANT = Object.fromEntries(BUCKETS.map((b) => [b.key, b.variant]));

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

const HRActionCenter = () => {
  const { user } = useAuth();
  const canApprove = APPROVER_ROLES.includes(user?.role);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [timelineFor, setTimelineFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.bucket = filter;
      const [pendRes, sumRes] = await Promise.all([
        actionCenterAPI.pending(params),
        actionCenterAPI.summary().catch(() => ({ data: {} })),
      ]);
      setItems(pendRes.data.items || []);
      setTotal(sumRes.data.total ?? (pendRes.data.total || 0));
      setCounts(sumRes.data.counts || pendRes.data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load action center');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // The only inline action that maps to a single existing endpoint is approving /
  // rejecting a pending lifecycle event; everything else deep-links to its module.
  const approveLifecycle = (item) => confirmToast({
    message: 'Approve this lifecycle action?',
    description: `${item.title} for ${item.employee_name || item.employee_id} — applies the change to the employee record.`,
    confirmLabel: 'Approve',
    onConfirm: async () => {
      try { await hrLifecycleAPI.approve(item.entity_id); toast.success('Approved'); load(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    },
  });
  const rejectLifecycle = (item) => confirmToast({
    message: 'Reject this lifecycle action?',
    confirmLabel: 'Reject',
    destructive: true,
    onConfirm: async () => {
      try { await hrLifecycleAPI.reject(item.entity_id, 'Rejected'); toast.success('Rejected'); load(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">HR Action Center</h2>
          <p className="text-sm text-muted-foreground">Everything awaiting an HR decision, across the entire employee lifecycle.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
      </div>

      {/* Summary cards double as bucket filters. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card
          className={`cursor-pointer transition-colors ${filter === 'all' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{total ?? 0}</div>
            <div className="text-xs text-muted-foreground">All Pending</div>
          </CardContent>
        </Card>
        {BUCKETS.map((b) => (
          <Card
            key={b.key}
            className={`cursor-pointer transition-colors ${filter === b.key ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter(b.key)}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{counts[b.key] ?? 0}</div>
              <div className="text-xs text-muted-foreground">{b.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead><TableHead>Employee</TableHead><TableHead>Category</TableHead>
                <TableHead>Detail</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  <Check className="h-5 w-5 mx-auto mb-2 text-green-600" />All clear — nothing awaiting action.
                </TableCell></TableRow>
              )}
              {!loading && items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="font-medium">{it.title}</TableCell>
                  <TableCell>
                    <button className="text-primary hover:underline text-left" onClick={() => setTimelineFor(it)}>
                      {it.employee_name || it.employee_id}
                    </button>
                  </TableCell>
                  <TableCell><Badge variant={BUCKET_VARIANT[it.bucket] || 'secondary'}>{BUCKET_LABEL[it.bucket] || it.bucket}</Badge></TableCell>
                  <TableCell className="max-w-[240px] truncate text-muted-foreground">{it.subtitle || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap"><Clock className="h-3 w-3 inline mr-1 text-muted-foreground" />{fmtDate(it.date)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" title="View timeline" onClick={() => setTimelineFor(it)}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    {it.bucket === 'lifecycle_approvals' && canApprove && (
                      <>
                        <Button variant="ghost" size="icon" title="Approve" onClick={() => approveLifecycle(it)}><Check className="h-4 w-4 text-green-600" /></Button>
                        <Button variant="ghost" size="icon" title="Reject" onClick={() => rejectLifecycle(it)}><X className="h-4 w-4 text-red-600" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && items.length > 0 && (
            <div className="text-xs text-muted-foreground mt-3">{items.length} item(s) shown.</div>
          )}
        </CardContent>
      </Card>

      {/* Unified employee timeline */}
      <Dialog open={!!timelineFor} onOpenChange={(o) => !o && setTimelineFor(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Timeline — {timelineFor?.employee_name || timelineFor?.employee_id}
            </DialogTitle>
          </DialogHeader>
          {timelineFor && <EmployeeTimeline employeeId={timelineFor.employee_id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRActionCenter;
